import React, { AnchorHTMLAttributes, FunctionComponent, PropsWithChildren, ReactNode, useState } from 'react';
import { Link } from 'react-router-dom';
import Icon, { IconName } from '../icons/icon/icon-component';
import styles from './pill-component.module.scss';

type PillProps = PropsWithChildren<
  {
    color?: 'white' | 'grey' | 'yellow' | 'blue' | string;
    class?: string;
    /**
     * 3d | account | add | add-cart | attributes | back | basket | bin |
     * done | chev-down | chev-left | chev-right | chev-up | close | compare |
     * cookie | cookie-blocked | cookie-whole | envelope | filter | grid |
     * info | insert | list | man | menu | more-hor | more-vert | pdf |
     * phone | search | settings | sort | sort-down | table |
     * visibility | visibility-off| website
     **/
    icon?: IconName;
    label?: ReactNode;
    isClosable?: boolean;
    isOnClickClosable?: boolean;
    isDisabled?: boolean;
    /**
     * if false, will use the react-router <Link> to redirect without refreshing
     */
    isExternalLink?: boolean;
    closeCallback?: (e: React.SyntheticEvent) => void;
  } & AnchorHTMLAttributes<HTMLAnchorElement>
>;

/**
 * Pill component
 *
 * https://www.figma.com/file/jiN4FkdkhDZIWxIkPS6Ru7/design-system?node-id=48%3A1182
 */
export const Pill: FunctionComponent<PillProps> = ({
  color = 'grey',
  class: classname = '',
  icon,
  label,
  children,
  isClosable = false,
  isOnClickClosable = false,
  isDisabled = false,
  isExternalLink = false,
  closeCallback,
  ...anchorAttributes
}) => {
  const [isHidden, setIsHidden] = useState(false);

  const pillColor = color ?? 'grey';

  const styleString = `${styles['pill-component']} ${isHidden ? 'hide' : ''} ${isClosable ? styles['closable'] : ''} ${
    styles[pillColor]
  } ${classname} ${isHidden ? 'hide' : ''} ${isDisabled ? styles.disabled : ''} ${isOnClickClosable ? styles['on-click'] : ''}`;

  if (!isOnClickClosable && (anchorAttributes as AnchorHTMLAttributes<HTMLAnchorElement>).href !== undefined) {
    const {
      href,
      className,
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      style,
      ...aAttributes
    } = anchorAttributes as AnchorHTMLAttributes<HTMLAnchorElement>;

    if (isExternalLink) {
      return (
        <a href={href} {...aAttributes} className={`${styleString} ${className}`}>
          {getPillContent()}
        </a>
      );
    }
    //internal link
    return (
      <Link to={href ?? ''} {...aAttributes} className={`${styleString} ${className}`}>
        {getPillContent()}
      </Link>
    );
  } else {
    return (
      <div
        onClick={(e) => {
          if (isOnClickClosable) onClose(e);
        }}
        className={styleString}
      >
        {getPillContent()}
      </div>
    );
  }

  function onClose(e: React.MouseEvent<SVGElement | HTMLDivElement, MouseEvent>) {
    setIsHidden(true);
    closeCallback?.(e);
  }

  function getPillContent() {
    return (
      <>
        {icon ? <Icon name={icon} className={styles.icon} /> : null}
        {label ? <span className={styles.text}>{label}</span> : ''}
        {children}
        {isClosable || isOnClickClosable ? (
          <Icon
            name='close'
            data-testid={`${color}_icon`}
            className={`${styles.icon} ${styles.close}`}
            onClick={(e) => {
              if (!isDisabled) onClose(e);
            }}
          />
        ) : null}
      </>
    );
  }
};

export default Pill;
